import { useEffect } from "react";

/**
 * Lets the sections of a page arrive as they are scrolled to.
 *
 * Elements marked data-reveal fade and rise into place once; elements marked
 * data-observe only receive the .is-in class, for effects of their own (the
 * rule that draws itself across the three steps).
 *
 * Nothing is hidden until this has run: what is already on screen is marked
 * as seen first, and only then is .reveal-ready set, which is the class the
 * stylesheet hides the rest behind. A page whose script never starts, or a
 * visitor who asked for less motion, sees everything in place.
 */
export function useReveal() {
  useEffect(() => {
    const root = document.documentElement;
    if (!root.classList.contains("motion-ok") || !("IntersectionObserver" in window)) return;

    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reveal], [data-observe]"),
    );
    const fold = window.innerHeight;
    for (const node of nodes) {
      if (node.getBoundingClientRect().top < fold) node.classList.add("is-in");
    }
    root.classList.add("reveal-ready");

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add("is-in");
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 },
    );
    for (const node of nodes) {
      if (!node.classList.contains("is-in")) observer.observe(node);
    }

    return () => {
      observer.disconnect();
      root.classList.remove("reveal-ready");
    };
  }, []);
}
